import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useShiftStats } from '../hooks/useShiftStats';
import BottomNav from '../components/BottomNav';
import { useNotifications } from '../hooks/useNotifications';

function fmt(n: number): string {
  return n.toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface ShiftModalProps {
  storeId: string;
  employeeId: string;
  onClose: () => void;
}

function ShiftCloseModal({ storeId, employeeId, onClose }: ShiftModalProps) {
  const { stats, loading } = useShiftStats(storeId, employeeId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.5)' }}
    >
      <div className="w-full md:max-w-md bg-surface" style={{ border: '0.5px solid #e5e7eb', maxHeight: '80vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
          <p className="text-sm font-bold text-on-surface-strong">Clôture shift</p>
          <button onClick={onClose} className="min-h-[36px] min-w-[36px] flex items-center justify-center">
            <span className="material-symbols-outlined text-[20px] text-on-surface-variant">close</span>
          </button>
        </div>
        {loading || !stats ? (
          <div className="p-8 text-center text-sm text-on-surface-variant">Chargement…</div>
        ) : (
          <div className="px-4 py-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-on-surface-variant">Total encaissé</span>
              <span className="text-sm font-bold tabular-nums">{fmt(stats.total_amount)} MAD</span>
            </div>
            {[
              { key: 'nfc', label: 'NFC', val: stats.nfc_amount },
              { key: 'qr', label: 'QR', val: stats.qr_amount },
              { key: 'cash', label: 'Cash', val: stats.cash_amount },
              { key: 'zazu', label: 'ZAZU', val: stats.zazu_amount },
            ].map((r) => (
              <div key={r.key} className="flex justify-between pl-4">
                <span className="text-xs text-on-surface-variant">Dont {r.label}</span>
                <span className="text-xs tabular-nums text-on-surface">{fmt(r.val)} MAD</span>
              </div>
            ))}
            <div className="flex justify-between pt-1" style={{ borderTop: '0.5px solid #e5e7eb' }}>
              <span className="text-xs text-on-surface-variant">Transactions</span>
              <span className="text-xs tabular-nums">{stats.tx_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-on-surface-variant">Tables servies</span>
              <span className="text-xs tabular-nums">{stats.tables_served}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-on-surface-variant">Ticket moyen</span>
              <span className="text-xs tabular-nums">{fmt(stats.avg_ticket)} MAD</span>
            </div>
            <button onClick={onClose} className="w-full min-h-[48px] text-xs font-semibold uppercase tracking-wider text-on-primary bg-primary mt-2">
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MyShiftPage() {
  const { session } = useAuth();
  const { stats, loading } = useShiftStats(session?.storeId ?? '', session?.employeeId ?? '');
  const { unreadCount } = useNotifications(session?.storeId ?? '');
  const [shiftOpen, setShiftOpen] = useState(false);

  const now = new Date();
  const dateLabel = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const total = stats?.total_amount ?? 0;
  const nfc = stats?.nfc_amount ?? 0;
  const qr = stats?.qr_amount ?? 0;
  const cash = stats?.cash_amount ?? 0;

  const nfcPct = total > 0 ? (nfc / total) * 100 : 0;
  const qrPct = total > 0 ? (qr / total) * 100 : 0;
  const cashPct = total > 0 ? (cash / total) * 100 : 0;

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface px-4 h-12 flex items-center justify-between"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <p className="text-sm font-bold text-on-surface-strong">Mon shift</p>
        <p className="text-xs text-on-surface-variant capitalize">{dateLabel}</p>
      </header>

      <div className="p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="h-8 w-8 animate-scanpay-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <>
            {/* Total */}
            <div className="bg-surface p-4" style={{ border: '0.5px solid #e5e7eb' }}>
              <p className="text-xs text-on-surface-variant mb-1">Total encaissé</p>
              <p className="text-[32px] font-bold tabular-nums text-on-surface-strong">{fmt(total)} MAD</p>
            </div>

            {/* Split */}
            <div className="grid grid-cols-3 gap-0" style={{ border: '0.5px solid #e5e7eb' }}>
              {[
                { label: 'NFC', val: nfc, pct: nfcPct },
                { label: 'QR', val: qr, pct: qrPct },
                { label: 'Cash', val: cash, pct: cashPct },
              ].map((col, i) => (
                <div
                  key={col.label}
                  className="p-3 flex flex-col gap-1"
                  style={{ borderRight: i < 2 ? '0.5px solid #e5e7eb' : undefined }}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant">{col.label}</p>
                  <p className="text-sm font-bold tabular-nums text-on-surface-strong">{fmt(col.val)}</p>
                  <p className="text-xs text-on-surface-variant">{col.pct.toFixed(0)}%</p>
                </div>
              ))}
            </div>

            {/* Barre split */}
            <div className="h-[8px] flex overflow-hidden bg-surface-container-high" style={{ border: '0.5px solid #e5e7eb' }}>
              <div className="h-full bg-primary" style={{ width: `${nfcPct}%` }} />
              <div className="h-full bg-on-surface-variant" style={{ width: `${qrPct}%` }} />
              <div className="h-full bg-outline-variant" style={{ width: `${cashPct}%` }} />
            </div>

            {/* Stats ligne */}
            {[
              { label: 'Transactions', val: String(stats?.tx_count ?? 0) },
              { label: 'Tables servies', val: String(stats?.tables_served ?? 0) },
              { label: 'Ticket moyen', val: `${fmt(stats?.avg_ticket ?? 0)} MAD` },
              { label: 'Pourboires', val: `${fmt(stats?.tips_total ?? 0)} MAD` },
            ].map((row, i, arr) => (
              <div
                key={row.label}
                className="flex items-center justify-between py-3 px-1"
                style={{ borderBottom: i < arr.length - 1 ? '0.5px solid #e5e7eb' : undefined }}
              >
                <span className="text-sm text-on-surface-variant">{row.label}</span>
                <span className="text-sm font-semibold tabular-nums text-on-surface-strong">{row.val}</span>
              </div>
            ))}

            {/* Bouton clôture (manager seulement) */}
            {session?.role === 'manager' && (
              <button
                onClick={() => setShiftOpen(true)}
                className="w-full min-h-[52px] flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-on-surface bg-surface mt-2"
                style={{ border: '0.5px solid #e5e7eb' }}
              >
                <span className="material-symbols-outlined text-[18px]">summarize</span>
                Clôturer mon shift
              </button>
            )}
          </>
        )}
      </div>

      {shiftOpen && session && (
        <ShiftCloseModal
          storeId={session.storeId}
          employeeId={session.employeeId}
          onClose={() => setShiftOpen(false)}
        />
      )}

      <BottomNav unreadCount={unreadCount} />
    </div>
  );
}
